import { useState } from 'react';
import { ModalTemplate } from '../components/ModalTemplate';
import { modal } from '../utils/modal';
import { AlertConfig } from './types';

export type AlertResult = { rememberAction: boolean } | false;

export const AlertDialog = ({
  title,
  errorMessage,
  body,
  showRememberActionCheckbox,
  rememberActionCustomCheckboxText,
  confirmButtonTitle = 'Close',
  confirmButtonClickId,
  isConfirmButtonDanger,
  onClose,
  ...restOfDialogBaseProps
}: AlertConfig & {
  onClose: (result: AlertResult) => void;
}) => {
  const [rememberAction, setRememberAction] = useState<boolean>(false);
  const onCloseProp = () =>
    onClose(showRememberActionCheckbox ? { rememberAction } : false);

  return (
    <ModalTemplate
      headerText={title}
      rememberActionCheckboxState={rememberAction}
      onRememberActionCheckboxChange={setRememberAction}
      rememberActionCustomCheckboxText={rememberActionCustomCheckboxText}
      showRememberActionCheckbox={showRememberActionCheckbox}
      errorBlockMessage={errorMessage}
      primaryButtonText={!isConfirmButtonDanger ? confirmButtonTitle : ''}
      primaryButtonClickId={confirmButtonClickId}
      onPrimaryButtonClick={onCloseProp}
      dangerButtonText={isConfirmButtonDanger ? confirmButtonTitle : ''}
      dangerButtonClickId={confirmButtonClickId}
      onDangerButtonClick={onCloseProp}
      {...restOfDialogBaseProps}
    >
      {body}
    </ModalTemplate>
  );
};

/**
 * Use this function when you would like to launch an alert dialog from
 * anywhere in the app. The function is async so it should be treated as that.
 * Returns a promise that will always resolve to `false` when user does an action.
 * @async
 * @param config Takes a config with the props needed to render the dialog.
 * @deprecated Avoid this in new code. Use `AnimatedModal` and control `isOpen`
 * like any other state. See #19797
 */
export const alert = ({
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  ...config
}: AlertConfig) => {
  return modal<AlertResult>(
    resolve => <AlertDialog onClose={result => resolve(result)} {...config} />,
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
